import { useCallback } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { useFtpStore } from '../store/ftpStore';
import { FTP_LIST_LIMIT } from './aiExtensionHelpers';

/** Terminal and FTP tools for the assistant: list connections, list a directory, run a command. */
export function useAiConnectionActions() {
  // ── Terminal: SSH over vault connections (ssh_term.rs) ──────────────────────
  // Privacy contract: the model only ever handles vault item IDS and command
  // OUTPUT. Credentials are resolved inside Rust (vault::ssh_credentials_for_
  // item) — there is no code path here that could hand the model a secret.

  const terminalListConnections = useCallback(async () => {
    await useFtpStore.getState().loadConnections().catch(() => {});
    const connections = useFtpStore
      .getState()
      .connections.filter(
        (c) => String(c.index?.protocol || 'sftp').toLowerCase() === 'sftp'
      )
      // conn_id and name ONLY (owner rule, 17.09.2026). What an agent needs is
      // to map "ws-de1" to something it can act with; the host and the account
      // it logs in as are not that, and handing them over turns a tool that
      // lists names into a directory of the user's servers and usernames - half
      // an SSH credential, and reconnaissance for anything that later reads a
      // tool result. The MCP surface already answers this way and says why:
      // `O40 - connId and name only`, src-tauri/src/mcp/terminal_tools.rs:98.
      // The approval card resolves the conn_id back to the saved entry and
      // shows the user `name (user@host:port)` (AiApprovalCard.terminalTarget):
      // the one who has to recognise the target is the human, and the model
      // can no longer name it for them.
      .map((c) => ({ conn_id: c.id, name: c.name }));
    return { connections };
  }, []);

  /**
   * The user's saved FTP/SFTP connections.
   *
   * Same shape as terminal_list_connections and for the same reason: the model
   * needs a conn_id to act with, and nothing else about a connection is its
   * business. No password, no key, no port — those live encrypted in the Vault
   * and never enter a tool result.
   */
  const ftpListConnections = useCallback(async () => {
    await useFtpStore.getState().loadConnections().catch(() => {});
    // Same rule as terminal_list_connections above, and the comment over this
    // function already claimed it: conn_id and name, nothing else about the
    // connection. `protocol` stays because it is not a credential and not about
    // the user - it tells the model whether this is FTP or SFTP, which decides
    // whether a tool applies at all.
    const connections = useFtpStore.getState().connections.map((c) => ({
      conn_id: c.id,
      name: c.name,
      protocol: String(c.index?.protocol || 'sftp').toLowerCase(),
    }));
    return { connections };
  }, []);

  /**
   * List a remote directory.
   *
   * Unlike the terminal, FTP does NOT auto-connect: ftp_list_dir starts with
   * `get_session(&conn_id)?` and fails outright if the user has not opened that
   * connection. Rather than surfacing a Rust error string, that case gets an
   * instruction the model can act on.
   *
   * Names only — no file contents. A remote listing is already
   * attacker-influenceable on a shared host, and the difference between a
   * filename and a file body is the difference between metadata and whatever
   * someone put in it.
   */
  const ftpListDir = useCallback(async (input = {}) => {
    const connId = String(input.conn_id || '');
    if (!connId) return { error: 'conn_id is required — call ftp_list_connections first' };
    const path = String(input.path || '/') || '/';

    let entries;
    try {
      entries = await invoke('ftp_list_dir', {
        connId,
        path,
        showHidden: Boolean(input.show_hidden),
      });
    } catch (e) {
      const message = String(e?.message || e || '');
      if (/session|not connected|no session/i.test(message)) {
        return { error: `Not connected to ${connId}. Open that connection in the FTP panel first — this tool cannot connect on its own.` };
      }
      return { error: `Could not list ${path}: ${message}` };
    }

    const all = Array.isArray(entries) ? entries : [];
    const capped = all.slice(0, FTP_LIST_LIMIT);
    return {
      path,
      total: all.length,
      truncated: all.length > FTP_LIST_LIMIT || undefined,
      entries: capped.map((e) => ({
        name: e.name,
        is_dir: Boolean(e.is_dir),
        size: Number(e.size ?? 0),
        // Deliberately not forwarded: mtime and permissions. Neither helps the
        // model answer "what is in this directory", and permissions in
        // particular is the kind of detail that invites it to suggest chmod.
      })),
    };
  }, []);

  const terminalRunCommand = useCallback(async (input) => {
    const connId = String(input?.conn_id || '');
    const command = String(input?.command || '');
    if (!connId || !command) return { error: 'conn_id and command are required' };
    const timeout = Number(input?.timeout_secs);
    // Rust mirrors the command + output into any open terminal tab for this
    // connection (ssh-term-ai-exec), so the user always sees what ran.
    const res = await invoke('ssh_term_exec', {
      connId,
      command,
      timeoutSecs: Number.isFinite(timeout) && timeout > 0 ? Math.min(timeout, 300) : 30,
    });
    return { stdout: res.stdout, stderr: res.stderr, exit_code: res.exitCode };
  }, []);

  return { terminalListConnections, ftpListConnections, ftpListDir, terminalRunCommand };
}
